import { useCallback, useMemo } from 'react';
import { UnifiedAuthView } from './UnifiedAccountView';
import { UnifiedHeadlessAuthView } from './UnifiedHeadlessAuthView';
import { createAuthConfigFetchers } from './helpers/createAuthConfigFetcher';
import { deleteAccount } from './api/UnifiedAccountApi';
import { captureException, toError } from '../internal/ravenUtils';
import { useCompleteWithCleanup } from '../shared/hooks';
import { scopesCacheKey } from '../shared/utils/scopesCacheKey';
import { jsx as _jsx } from "react/jsx-runtime";
export function UnifiedAccountModal(props) {
  const {
    inline = false,
    portalId,
    appId,
    appName,
    oAuthScope,
    oAuthScopes,
    origin,
    accountId,
    mcpParams,
    customParams,
    oAuthorMigrationParams,
    incremental,
    onComplete,
    onCancel,
    onAuthError,
    config
  } = props;
  const {
    shouldDeleteOnFailure,
    useIntroScreen,
    useSuccessScreen
  } = config !== null && config !== void 0 ? config : {};
  const oAuthScopesCacheKey = scopesCacheKey(oAuthScopes);

  // Serialised to a string to avoid object reference instability in the dep array
  const customParamsCacheKey = customParams ? JSON.stringify(customParams) : undefined;
  const fetchers = useMemo(() => createAuthConfigFetchers({
    portalId,
    appId,
    appName,
    oAuthScope,
    oAuthScopes,
    origin,
    mcpParams: mcpParams !== null && mcpParams !== void 0 ? mcpParams : undefined,
    incremental,
    customParams
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [portalId, appId, appName, oAuthScope, oAuthScopesCacheKey, origin, mcpParams, incremental, customParamsCacheKey]);
  const isMigrationReconnect = !!oAuthorMigrationParams;
  const isReauth = !isMigrationReconnect && !!accountId;
  const isReconnect = isMigrationReconnect || isReauth;
  const completeWithCleanup = useCompleteWithCleanup(onComplete, useCallback(newAccountId =>
  // Reauth flows operate on a pre-existing account: a failure here
  // must not delete it or trigger downstream data cleanup. Migration
  // reconnect creates a brand-new account under the hood, so it keeps
  // the normal rollback behaviour.
  !isReauth && (shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true), [isReauth, shouldDeleteOnFailure]), useCallback(newAccountId => {
    void deleteAccount(newAccountId);
  }, []), useCallback((error, newAccountId) => {
    captureException(new Error('onComplete callback failed after account creation', {
      cause: toError(error)
    }), {
      extra: {
        appId,
        accountId: newAccountId
      }
    });
  }, [appId]));
  const handleComplete = useCallback(({
    accountId: newAccountId
  }) => completeWithCleanup(newAccountId), [completeWithCleanup]);
  const fetchAuthConfig = isMigrationReconnect ? () => fetchers.forMigrationReconnect(oAuthorMigrationParams === null || oAuthorMigrationParams === void 0 ? void 0 : oAuthorMigrationParams.oAuthorAccountId) : isReauth ? () => fetchers.forReconnect(accountId) : fetchers.forNewAccount;
  if (props.mode === 'headless') {
    return /*#__PURE__*/_jsx(UnifiedHeadlessAuthView, {
      portalId: portalId,
      appName: appName,
      isReauth: isReconnect,
      accountId: isReauth ? accountId : undefined,
      onComplete: handleComplete,
      onCancel: onCancel,
      onAuthError: onAuthError,
      useIntroScreen: useIntroScreen,
      useSuccessScreen: useSuccessScreen,
      fetchAuthConfig: fetchAuthConfig
    });
  }
  const modeProps = inline ? {
    mode: 'inline'
  } : props.mode === 'panel' ? {
    mode: 'panel',
    modeSettings: props.modeSettings
  } : {
    mode: 'modal',
    modeSettings: props.modeSettings
  };
  return /*#__PURE__*/_jsx(UnifiedAuthView, Object.assign({}, modeProps, {
    isReauth: isReconnect,
    portalId: portalId,
    accountId: isReauth ? accountId : undefined,
    appName: appName,
    onComplete: handleComplete,
    onCancel: onCancel,
    useIntroScreen: useIntroScreen,
    useSuccessScreen: useSuccessScreen,
    onAuthError: onAuthError,
    fetchAuthConfig: fetchAuthConfig
  }));
}
export default UnifiedAccountModal;